"use client";

import React from "react";
import { Link } from "@/components/common/Link";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { AspectRatio } from "@/components/ui/aspect-ratio";
import { Pagination, PaginationContent, PaginationItem } from "@/components/ui/pagination";
function PostGridConsumer() {
  const {
    posts,
    loading,
    error,
    hasNext,
    hasPrev,
    nextPage,
    prevPage,
    isRefetching
  } = usePosts();
  if (loading) {
    return <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10 lg:gap-14">
        {[...Array(6)].map((_, i) => <Card key={i} data-index={i} className="border-none shadow-none bg-transparent animate-pulse">
            <AspectRatio ratio={4 / 3} className="bg-muted rounded-xl mb-6" />
            <div className="h-6 bg-muted rounded w-3/4 mb-4" />
            <div className="h-4 bg-muted rounded w-full mb-2" />
            <div className="h-4 bg-muted rounded w-2/3" />
          </Card>)}
      </div>;
  }
  if (error) {
    return <div className="text-center py-16 text-destructive font-sans">
        Failed to load articles. Please try again later.
      </div>;
  }
  if (posts.length === 0) {
    return <div className="text-center py-16 text-muted-foreground font-serif text-xl">
        No articles published yet.
      </div>;
  }
  return <div className="space-y-16">
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10 lg:gap-14">
        {posts.map((post, i) => <Card key={post.id} data-index={i} className="border-none shadow-none bg-transparent group flex flex-col h-full">
            {post.featuredImage && <Link to={post.link || "#"} className="block mb-6 overflow-hidden rounded-xl">
                <AspectRatio ratio={4 / 3}>
                  <img src={post.featuredImage} alt={post.title} className="object-cover w-full h-full transition-transform duration-700 ease-out group-hover:scale-105" loading="lazy" />
                </AspectRatio>
              </Link>}
            <CardHeader className="p-0 mb-4">
              <CardTitle className="font-default text-2xl font-medium leading-snug text-foreground transition-colors duration-300 group-hover:text-[var(--color-green-accent)]">
                <Link to={post.link || "#"}>{post.title}</Link>
              </CardTitle>
            </CardHeader>
            <CardContent className="p-0 flex-grow">
              {post.excerpt && <div className="text-muted-foreground font-sans leading-relaxed line-clamp-3" dangerouslySetInnerHTML={{
            __html: post.excerpt
          }} />}
            </CardContent>
            <CardFooter className="p-0 mt-8">
              <Link to={post.link || "#"}>
                <Button className="rounded-full px-8 py-6 font-sans text-sm tracking-wide bg-[var(--color-green-accent)] text-white hover:bg-[var(--color-green-accent)]/90 transition-all duration-300 border-none">
                  Read More
                </Button>
              </Link>
            </CardFooter>
          </Card>)}
      </div>

      {(hasNext || hasPrev) && <Pagination className="pt-8 border-t border-border/50">
          <PaginationContent className="gap-4">
            <PaginationItem>
              <Button variant="outline" onClick={prevPage} disabled={!hasPrev || isRefetching} className="rounded-full px-6 font-sans hover:bg-[var(--color-green-accent)] hover:text-white hover:border-[var(--color-green-accent)] transition-colors">
                Previous
              </Button>
            </PaginationItem>
            <PaginationItem>
              <Button variant="outline" onClick={nextPage} disabled={!hasNext || isRefetching} className="rounded-full px-6 font-sans hover:bg-[var(--color-green-accent)] hover:text-white hover:border-[var(--color-green-accent)] transition-colors">
                Next
              </Button>
            </PaginationItem>
          </PaginationContent>
        </Pagination>}
    </div>;
}
export default function PostFeedSection() {
  const wp_query_params = {
    post_type: "post",
    per_page: 6,
    order: "desc",
    orderby: "date"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section id="post-feed-section" className="bg-background py-20 md:py-32">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="mb-16 md:mb-24 text-center max-w-2xl mx-auto">
          <h2 className="font-default text-4xl md:text-5xl font-semibold text-foreground mb-6 tracking-tight">
            The Journal
          </h2>
          <p className="text-muted-foreground font-sans text-lg leading-relaxed">
            Curated insights, stories, and perspectives reflecting our commitment to excellence and modern luxury.
          </p>
        </div>
        
        <PostsProvider wp_query={{
        post_type: "post",
        per_page: 6,
        order: "desc",
        orderby: "date"
      }}>
          <PostGridConsumer />
        </PostsProvider>
      </div>
    </section>;
}